import { contentAssetUrl } from "game-common/content/catalog"
import { type IMediaInstance, sound } from "@pixi/sound"
import { ItemTypeMeta } from "game-common/item/item"
import { Entity, EntityId, Latch, randomBool, randomFloatBetween } from "game-common/models"
import { randomOneOf } from "game-common/util"
import { Loader } from "pixi.js"

import { ClientGameLogic } from "./client_game_logic"
import { Thunder } from "./audio/thunder"
import { BonecallerChant } from "./audio/bonecaller_chant"
import { EmberCoreCharge } from "./audio/ember_core_charge"

interface FootstepMeta {
    latch: Latch
    soundIdx: number
    soundIdxMax: number
}

interface SoundtrackMeta {
    trackId: string
    player: IMediaInstance
}

export class Audio {
    private logic: ClientGameLogic
    private walkSoundEffectLatch: Map<EntityId, FootstepMeta> = new Map()
    private playingSoundtrack: Record<string, SoundtrackMeta> = {}
    private static soundOptions: Record<string, any> = {}
    private thunder = new Thunder()
    private volume: number = 0.7
    private devSoundEnabled: boolean = false
    private devSoundHinted: boolean = false
    private commandSoundEnabled?: boolean
    private bonecallerAudioConnected = true
    private bonecallerChant = new BonecallerChant(() =>
        sound.play("bonecaller_chant", { loop: true, volume: 0 }) as IMediaInstance | Promise<IMediaInstance>)

    // The Ember Core's charge loop, and the hold it is currently following. The anchor is this
    // class's own copy of the trick every charged-weapon renderer plays: the server's remainingMs
    // is re-anchored to local time when it changes and counted down locally in between, so the
    // pitch ramp is continuous instead of stepping once per message.
    private emberCoreCharge = new EmberCoreCharge(() =>
        sound.play("ember_core_charge", { loop: true, volume: 0, speed: 0.72 }) as IMediaInstance | Promise<IMediaInstance>)
    private emberChargeAnchor?: { id: string; remainingMs: number; at: number }
    private emberReadyAnnounced?: string

    constructor(logic: ClientGameLogic) {
        this.logic = logic
        this.installDevSoundToggle()
        if (typeof document !== "undefined") document.addEventListener("visibilitychange", () => {
            if (document.hidden) {
                this.thunder.stop()
                this.stopBonecallerChant()
                this.stopEmberCoreCharge()
            }
        })
    }

    /** Enable or mute all client effects and music. */
    setSoundEnabled = (enabled: boolean) => {
        this.commandSoundEnabled = enabled
        if (enabled) {
            sound.unmuteAll()
            return
        }

        this.thunder.stop()
        sound.muteAll()
        this.stopBonecallerChant()
        this.stopEmberCoreCharge()
    }

    // Sound is off outside production so a dev session isn't noisy by default -- which also makes
    // it impossible to hear an effect you're working on. `devSound` opens it back up; see
    // installDevSoundToggle. Production is always audible and ignores the switch.
    private audible = (): boolean => {
        if (this.commandSoundEnabled !== undefined) {
            return this.commandSoundEnabled
        }
        if (this.logic.runLevel === "prod" || this.devSoundEnabled) {
            return true
        }
        // Say why exactly once, the first time something tries to make a noise, so the silence
        // isn't a mystery to anyone who doesn't already know the toggle exists.
        if (!this.devSoundHinted) {
            this.devSoundHinted = true
            console.log("[devSound] sound is muted outside production -- set `devSound = true` to hear it")
        }
        return false
    }

    // Browser-console switch, in the same shape as window.playerRing / window.spriteShadow:
    //
    //     devSound = true     // unmute this session
    //     devSound = 0.3      // unmute at 30% volume
    //     devSound = false    // mute again
    //     devSound            // read the current state back
    //
    // Dev only: in production sound is already on, so the setter refuses rather than pretending
    // to do something. Guarded so it is a no-op in the Node test harness.
    private installDevSoundToggle = () => {
        if (typeof window === "undefined") {
            return
        }
        Object.defineProperty(window, "devSound", {
            get: () => ({ enabled: this.devSoundEnabled, volume: this.volume }),
            set: (next: boolean | number) => {
                if (this.logic.runLevel === "prod") {
                    console.log("[devSound] production already plays sound; nothing to toggle")
                    return
                }
                if (typeof next === "number") {
                    this.devSoundEnabled = next > 0
                    this.volume = Math.max(0, Math.min(1, next))
                } else {
                    this.devSoundEnabled = !!next
                }
                console.log("[devSound]", { enabled: this.devSoundEnabled, volume: this.volume })
                if (!this.devSoundEnabled) {
                    this.thunder.stop()
                    this.stopBonecallerChant()
                    this.stopEmberCoreCharge()
                }
            },
            configurable: true,
        })
    }

    static loadAssets = () => {
        // sound effects
        // for (let i = 0; i <= 8; i++) {
        //     Loader.shared.add(`footsteps${i}`, contentAssetUrl(`assets/boots/${i}.ogg`))
        // }
        for (let i = 0; i <= 1; i++) {
            Loader.shared.add(`footsteps${i}`, contentAssetUrl(`assets/grass/${i}.ogg`))
        }
        for (let i = 0; i <= 1; i++) {
            Loader.shared.add(`swamp${i}`, contentAssetUrl(`assets/swamp/${i}.ogg`))
        }

        const audioPopulator = (category: string, action: string, numAssets: number, extension: string = "mp3") => {
            for (let i = 1; i <= numAssets; i++) {
                Loader.shared.add(`${category}-${action}-${i}`, contentAssetUrl(`assets/${category}/${action}/${i}.${extension}`))
            }
        }
        // audioPopulator("zombie", "attack", 7)
        // audioPopulator("zombie", "death", 6)
        // audioPopulator("zombie", "detect", 5)
        // audioPopulator("zombie", "hit", 6)
        // audioPopulator("human", "death", 8, "ogg")

        // soundtrack
        // Loader.shared.add('dome1.ogg', 'assets/dome1.ogg')
        Loader.shared.add("overworld1.ogg", contentAssetUrl("assets/overworld1.ogg"))
        Loader.shared.add("overworld-day-noises.ogg", contentAssetUrl("assets/overworld-day-noises.ogg"))
        Loader.shared.add("overworld-night-noises.ogg", contentAssetUrl("assets/overworld-night-noises.ogg"))
        Loader.shared.add("dungeon-ambience.ogg", contentAssetUrl("assets/dungeon-ambience.ogg"))
        Loader.shared.add("door-open.ogg", contentAssetUrl("assets/door-open.ogg"))
        Loader.shared.add("to_battle.ogg", contentAssetUrl("assets/to_battle.ogg"))

        Audio.soundOptions["overworld1.ogg"] = {
            volume: 0.15,
        }
        Audio.soundOptions["overworld-day-noises.ogg"] = {
            volume: 3.8,
        }
        Audio.soundOptions["overworld-night-noises.ogg"] = {
            volume: 0.25,
        }
        Audio.soundOptions["dungeon-ambience.ogg"] = {
            volume: 0.5,
        }
        Audio.soundOptions["powerup"] = {
            volume: 0.5,
        }
        Audio.soundOptions["door_close"] = {
            volume: 0.05,
        }
        Audio.soundOptions["door_open"] = {
            volume: 0.05,
        }
        Audio.soundOptions["door-open.ogg"] = {
            volume: 0.25,
        }

        // Procedurally synthesized one-shots (packages/tools/src/sfx/gen.py). These are peak
        // normalized in the generator, so the balance against the older, quieter assets lives
        // here rather than in the files.
        Audio.soundOptions["campfire_lit"] = {
            volume: 0.3,
        }
        Audio.soundOptions["campfire_fed"] = {
            volume: 0.25,
        }
        Audio.soundOptions["wheel_select"] = {
            volume: 0.12,
        }
        Audio.soundOptions["item_crafted"] = {
            volume: 0.25,
        }
        Audio.soundOptions["wave_starting"] = {
            volume: 0.4,
        }
        // Quieter than the wave horn by a long way, on purpose: a stranger at the fire is a thing
        // to notice, not a thing to be startled by, and it can land while the player is mid-fight.
        Audio.soundOptions["visitor_arrives"] = {
            volume: 0.22,
        }
        // The story checklist's rewards. The tick is high and bright, so it sits a shade under
        // item_crafted to land at the same loudness; the stamp is an afterthought to it and stays
        // down at the command wheel's level. The chapter fanfare is the one that is meant to be
        // heard over a camp -- a little above the wave horn, and it comes once per chapter.
        Audio.soundOptions["chapter_task_complete"] = { volume: 0.24 }
        Audio.soundOptions["chapter_task_stamp"] = { volume: 0.16 }
        Audio.soundOptions["chapter_complete"] = { volume: 0.4 }
        // The chapter title card: its crest catching light, then the Begin press answering it.
        Audio.soundOptions["chapter_announce"] = { volume: 0.3 }
        Audio.soundOptions["chapter_begin"] = { volume: 0.26 }
        Audio.soundOptions["bonecaller_spike_windup"] = { volume: 0.22 }
        Audio.soundOptions["bonecaller_spike_impact"] = { volume: 0.4 }
        Audio.soundOptions["bonecaller_sweep_windup"] = { volume: 0.2 }
        Audio.soundOptions["bonecaller_sweep_impact"] = { volume: 0.35 }
        Audio.soundOptions["ground_rupture"] = { volume: 0.22 }
        // A planned build turning solid. It comes every few seconds while a camp is being laid out,
        // so it sits with the short UI ticks (wheel_select, chapter_task_stamp), a shade above them.
        Audio.soundOptions["build_printed"] = { volume: 0.2 }
        // A felled tree: the hinge giving way, then the crash. The crash is the payoff of a run of axe
        // strokes, so it is the louder of the two -- about a blow's woodhit plus half again -- but it
        // stays well under the blasts: in a forest camp it comes several times a minute.
        Audio.soundOptions["tree_creak"] = { volume: 0.22 }
        Audio.soundOptions["tree_fall"] = { volume: 0.34 }

        // The Ember Core. The blast is the loudest thing the game makes on purpose -- it is a
        // once-every-two-seconds, ammunition-limited payoff, and it has to beat a wave of enemies
        // and a burning camp. The fuse is quiet by comparison because it plays UNDER the moment the
        // player is reading the warning circle, and a loud sizzle there just masks the blast.
        // These are the base levels; the one-shots scale them by distance at the call site.
        Audio.soundOptions["ember_core_blast"] = {
            volume: 0.55,
        }
        Audio.soundOptions["ember_core_fire"] = {
            volume: 0.3,
        }
        Audio.soundOptions["ember_core_fuse"] = {
            volume: 0.18,
        }
        Audio.soundOptions["ember_core_ready"] = {
            volume: 0.22,
        }
        Audio.soundOptions["ember_core_charge"] = {
            volume: 0.3,
        }
    }

    walking = (entity: Entity) => {
        //
    }

    stopBonecallerChant = () => this.bonecallerChant.stop()

    setBonecallerAudioConnected = (connected: boolean) => {
        this.bonecallerAudioConnected = connected
        if (!connected) this.stopBonecallerChant()
    }

    updateBonecallerChant = (now = Date.now()) => {
        const listener = this.logic.playerEntity
        if (!this.bonecallerAudioConnected || !listener || listener.hp <= 0 || !this.logic.roomId || (typeof document !== "undefined" && document.hidden)) {
            this.stopBonecallerChant(); return
        }
        let shrine: Entity | undefined, nearest = Infinity
        this.logic.entityManager.iterate(null, entity => {
            if (entity.roomId !== this.logic.roomId) return
            if (entity.bonecallerRitual?.phase !== "active") return
            const distance = Math.hypot(entity.location.x - listener.location.x, entity.location.y - listener.location.y)
            if (distance < nearest) { shrine = entity; nearest = distance }
        })
        if (!shrine || !this.audible()) { this.stopBonecallerChant(); return }
        const stageVolume = [0, 0.09, 0.14, 0.2][shrine.bonecallerRitual.stage]
        const attenuation = Math.max(0, 1 - nearest / 640) ** 2
        this.bonecallerChant.update(`${this.logic.roomId}:${shrine.entityId}`, stageVolume * attenuation * this.volume, now)
    }

    stopEmberCoreCharge = () => {
        this.emberCoreCharge.stop()
        this.emberChargeAnchor = undefined
        this.emberReadyAnnounced = undefined
    }

    /**
     * How loud something happening at `source` should be for the player. Squared falloff, because
     * linear makes everything in the room sound like it is on top of you, and a floor of zero so a
     * blast across the camp is genuinely silent rather than a mystery thud.
     */
    private distanceGain = (source: { x: number; y: number } | undefined, range: number) => {
        const listener = this.logic.playerEntity?.location
        if (!source || !listener) return 1
        return Math.max(0, 1 - Math.hypot(source.x - listener.x, source.y - listener.y) / range) ** 2
    }

    /**
     * The furnace loop under a charged weapon being held, and the cue when it finishes charging.
     *
     * Follows the NEAREST charging entity rather than only the local player: an ally winding up an
     * Ember Core beside you is worth hearing, and a shot about to be thrown at you even more so.
     * One loop, as with the ritual chant -- two people charging is one furnace getting louder, which
     * is the right answer anyway.
     */
    updateEmberCoreCharge = (now = Date.now()) => {
        const listener = this.logic.playerEntity
        if (!listener || listener.hp <= 0 || !this.logic.roomId || !this.audible() ||
            (typeof document !== "undefined" && document.hidden)) {
            this.stopEmberCoreCharge(); return
        }
        let charging: Entity | undefined, nearest = Infinity
        this.logic.entityManager.iterate(null, entity => {
            if (entity.roomId !== this.logic.roomId || !entity.weaponCharge) return
            const distance = Math.hypot(entity.location.x - listener.location.x, entity.location.y - listener.location.y)
            if (distance < nearest) { charging = entity; nearest = distance }
        })
        const view = charging?.weaponCharge
        if (!view) { this.stopEmberCoreCharge(); return }
        // Re-anchor whenever the server restates the hold, then count down locally in between.
        const anchor = this.emberChargeAnchor
        if (!anchor || anchor.id !== view.id || anchor.remainingMs !== view.remainingMs) {
            this.emberChargeAnchor = { id: view.id, remainingMs: view.remainingMs, at: now }
        }
        const remaining = Math.max(0, this.emberChargeAnchor.remainingMs - now + this.emberChargeAnchor.at)
        const progress = view.durationMs ? Math.max(0, Math.min(1, 1 - remaining / view.durationMs)) : 1
        const attenuation = this.distanceGain(charging.location, 520)
        const base = Audio.soundOptions["ember_core_charge"]?.volume || 0.3
        // Gain and pitch both climb with the hold; the loop itself is flat (see gen.py).
        this.emberCoreCharge.update(`${this.logic.roomId}:${charging.entityId}:${view.id}`,
            base * (0.35 + 0.65 * progress) * attenuation * this.volume, 0.72 + 0.5 * progress, now)
        if (progress >= 1 && this.emberReadyAnnounced !== view.id) {
            this.emberReadyAnnounced = view.id
            this.play("ember_core_ready", {
                volume: (Audio.soundOptions["ember_core_ready"]?.volume || 0.22) * Math.max(0.3, attenuation),
            })
        }
        if (progress < 1 && this.emberReadyAnnounced === view.id) this.emberReadyAnnounced = undefined
    }

    groundRupture = (at: { x: number; y: number }) => {
        const gain = this.distanceGain(at, 640)
        if (gain > 0) this.play("ground_rupture", {
            volume: (Audio.soundOptions["ground_rupture"]?.volume || 0.22) * gain,
        })
    }

    // The three moments of a shot, routed here from the projectile's renderable through
    // effects/ember_core_feedback. All three are positional: a blast on the far side of the camp
    // should not sound like one at your feet.
    bombLanded = (at: { x: number; y: number }) => {
        this.play("clocktick", { volume: 0.25 * this.distanceGain(at, 440) })
    }

    bombBlast = (at: { x: number; y: number }) => {
        this.play("explosion", { volume: 0.5 * this.distanceGain(at, 1000) })
    }

    emberCoreFired = (at: { x: number; y: number }) => {
        this.play("ember_core_fire", {
            volume: (Audio.soundOptions["ember_core_fire"]?.volume || 0.3) * this.distanceGain(at, 700),
            speed: 0.97 + (randomBool() ? -1 : 1) * randomFloatBetween(0.0, 0.05),
        })
    }

    emberCoreLanded = (at: { x: number; y: number }) => {
        this.play("ember_core_fuse", {
            volume: (Audio.soundOptions["ember_core_fuse"]?.volume || 0.18) * this.distanceGain(at, 440),
        })
    }

    emberCoreBlast = (at: { x: number; y: number }) => {
        this.play("ember_core_blast", {
            volume: (Audio.soundOptions["ember_core_blast"]?.volume || 0.55) * Math.max(0.08, this.distanceGain(at, 1100)),
            speed: 0.95 + (randomBool() ? -1 : 1) * randomFloatBetween(0.0, 0.06),
        })
    }

    emp = (entity: Entity) => {
        if (!this.logic.isTooDistantFromPlayer(entity)) {
            this.play("emp", {
                volume: 0.25,
            })
        }
    }

    attack = (entity: Entity) => {
        if (!this.logic.isTooDistantFromPlayer(entity)) {
            this.play(["bow", "crossbow"].includes(entity.weapon?.weaponType) ? "bowshot" : "swordswing", {
                volume: 0.05,
                speed: 0.85 + (randomBool() ? -1 : 1) * randomFloatBetween(0.0, 0.03),
            })
        }
    }

    hit = (entity: Entity) => {
        this.play(`hit${randomOneOf([1, 2])}`, {
            volume: 0.075,
            speed: 0.75 + Math.min(0.25, Math.random()),
        })

        // if (entity.npcType === "zombie") {
        //     if (randomIntBetween(0, 2) === 0) {
        //         this.play(`zombie-hit-${randomIntBetween(1, 6)}`, {
        //             volume: 0.20,
        //             speed: 0.85 + Math.min(0.15, Math.random())
        //         })
        //     }
        // }
    }

    death = (entity: Entity) => {
        // if (entity.npcType === "zombie") {
        //     if (randomBool()) {
        //         this.play(`zombie-death-${randomIntBetween(1, 6)}`, {
        //             volume: 0.20,
        //             speed: 0.85 + Math.min(0.15, Math.random())
        //         })
        //     }
        // }
    }

    destroy = (entity: Entity) => {
        this.walkSoundEffectLatch.delete(entity.entityId)
    }

    walk = (entity: Entity) => {
        const baseline = 300
        let meta = this.walkSoundEffectLatch.get(entity.entityId)
        if (!meta) {
            meta = {
                latch: new Latch(baseline),
                soundIdx: -1,
                soundIdxMax: 2,
            }
            this.walkSoundEffectLatch.set(entity.entityId, meta)
        }
        meta.latch.update(baseline)
        if (meta.latch.expired()) {
            meta.soundIdx++
            if (meta.soundIdx > meta.soundIdxMax - 1) {
                meta.soundIdx = 0
            }
            const footstep = entity.submerged ? `swamp${meta.soundIdx}` : `footsteps${meta.soundIdx}`
            this.play(footstep, {
                volume: 0.08 + (randomBool() ? -1 : 1) * randomFloatBetween(0.01, 0.03),
                speed: 0.85 + (randomBool() ? -1 : 1) * randomFloatBetween(0.0, 0.15),
            })
        }
    }

    // Command-wheel / role-picker confirmation. Pitched a touch at random so repeated
    // selections don't sound machine-gunned.
    // Somebody has walked in out of the cold and is standing at the camp's fire asking to be taken
    // on. Played off the roster, not off the world, because the whole point of it is to reach a
    // player who is nowhere near the fire -- so unlike the hit and attack sounds it is deliberately
    // NOT gated on distance from the listener.
    visitorArrives = () => {
        this.play("visitor_arrives")
    }

    // A chapter-checklist task was just completed: the reward itself, played the instant it happens.
    chapterTaskComplete = () => {
        this.play("chapter_task_complete")
    }

    // ...and its gold check landing in the checklist row a beat later. Quiet on purpose: it is the
    // same reward settling, not a second one.
    chapterTaskStamp = () => {
        this.play("chapter_task_stamp")
    }

    // The last task of a chapter is done. Once per chapter, so it is allowed to be the big one.
    chapterComplete = () => {
        this.play("chapter_complete")
    }

    // A new chapter's title card: its crest igniting (a chord left hanging), and Begin answering it.
    chapterAnnounce = () => {
        this.play("chapter_announce")
    }

    chapterBegin = () => {
        this.play("chapter_begin")
    }

    // A planned build group was printed (the server accepted it). Played once per group, however
    // many tiles it held: a bigger group lands lower and fuller, so a whole room going up sounds
    // heavier than one wall piece (the drop bottoms out at 32 tiles, ~12% down and ~35% louder).
    // Pitch is jittered so a run of prints doesn't sound machine-gunned.
    buildPrinted = (tiles = 1) => {
        const weight = Math.min(1, Math.log2(Math.max(1, tiles)) / 5)
        this.play("build_printed", {
            volume: (Audio.soundOptions["build_printed"]?.volume || 0.2) * (1 + 0.35 * weight),
            speed: 1.03 - 0.12 * weight + randomFloatBetween(-0.03, 0.03),
        })
    }

    // A felled tree's two moments (effects/tree_fall.ts, through tree_fall_feedback). Positional: a
    // woodcutter felling across the camp is a distant crack, not one at your elbow. `weight` is ~1 for
    // a full-size tree, so a sapling lands lighter and higher.
    treeCreak = (at: { x: number; y: number }) => {
        const gain = this.distanceGain(at, 560)
        if (gain > 0) this.play("tree_creak", {
            volume: (Audio.soundOptions["tree_creak"]?.volume || 0.22) * gain,
            speed: 0.96 + randomFloatBetween(0, 0.08),
        })
    }

    treeFall = (at: { x: number; y: number }, weight = 1) => {
        const gain = this.distanceGain(at, 720)
        if (gain > 0) this.play("tree_fall", {
            volume: (Audio.soundOptions["tree_fall"]?.volume || 0.34) * gain * (0.6 + 0.4 * Math.min(1.2, weight)),
            speed: 1.08 - 0.12 * Math.min(1, weight) + randomFloatBetween(-0.03, 0.03),
        })
    }

    uiSelect = () => {
        this.play("wheel_select", {
            speed: 1.0 + (randomBool() ? -1 : 1) * randomFloatBetween(0.0, 0.04),
        })
    }

    // The pointer crossing onto another command-wheel order: the select bell, pitched well up and
    // barely there, so aiming ticks under the hand and the pick that follows sounds like the same
    // object answering. Played on every crossing, so it must stay quiet.
    uiHover = () => {
        this.play("wheel_select", {
            volume: (Audio.soundOptions["wheel_select"]?.volume || 0.12) * 0.3,
            speed: 1.75 + randomFloatBetween(0.0, 0.06),
        })
    }

    mapLocationFound = () => {
        this.play("map_location", {
            volume: 0.5,
        })
    }

    pickup = (itemTypeMeta: ItemTypeMeta) => {
        if (itemTypeMeta.type === "shard-metal") {
            this.play("coins", {
                volume: 0.1,
            })
        } else {
            this.play("cloth", {
                volume: 0.05,
            })
        }
    }

    static isSoundtrack = (effect: string) => {
        if (!effect) {
            return true
        }
        if (effect === "overworld1.ogg") {
            return true
        }
        if (effect === "overworld-day-noises.ogg") {
            return true
        }
        if (effect === "overworld-night-noises.ogg") {
            return true
        }
        if (effect === "dungeon-ambience.ogg") {
            return true
        }
        if (effect === "to_battle.ogg") {
            return true
        }

        return false
    }

    soundEffect = async (effect: string, loop: boolean, target: string, source?: { x: number; y: number }) => {
        if (!this.audible()) {
            return
        }
        if (["bonecaller_spike_windup", "bonecaller_spike_impact",
            "bonecaller_sweep_windup", "bonecaller_sweep_impact"].includes(effect)) {
            const gain = source ? this.distanceGain(source, 640) : 1
            if (gain > 0) this.play(effect, { volume: (Audio.soundOptions[effect]?.volume || 0.25) * gain })
            return
        }
        if (effect === "bonecaller_complete") {
            const listener = this.logic.playerEntity?.location
            const distance = source && listener ? Math.hypot(source.x - listener.x, source.y - listener.y) : 0
            this.play(effect, { volume: 0.5 * Math.max(0.2, 1 - distance / 960) })
            return
        }
        if (Audio.isSoundtrack(effect)) {
            const targetSoundtrack = this.playingSoundtrack[target]

            if (targetSoundtrack && (!effect || targetSoundtrack?.trackId !== effect)) {
                // if no effect, or its a different track for the target
                // then halt it
                if (!effect) {
                    delete this.playingSoundtrack[target]
                }
                const player = targetSoundtrack?.player

                // halt the currently played soundtrack
                if (player) {
                    const maxVolume = player.volume
                    const incr = Math.max(0.01, maxVolume / 50)
                    let prev = -1
                    const volInterval = setInterval(() => {
                        if (player.volume < 0.01 || prev === player.volume) {
                            clearInterval(volInterval)
                            player.stop()
                            return
                        }

                        prev = player.volume
                        player.set("volume", player.volume - incr)
                    }, 50)
                }
            }

            if (!effect) {
                return
            }

            if (targetSoundtrack && targetSoundtrack.trackId === effect) {
                // soundtrack already playing for the target
                return
            }

            const targetVolume = (Audio.soundOptions[effect]?.volume || 1.0) * this.volume
            const incr = Math.max(0.01, targetVolume / 50)
            const track: SoundtrackMeta = {
                trackId: effect,
                player: null,
            }
            this.playingSoundtrack[target] = track

            const instance = await sound.play(effect, {
                ...(Audio.soundOptions[effect] || {}),
                volume: 0.01 * this.volume,
                loop,
            })
            const volInterval = setInterval(() => {
                if (instance.volume >= targetVolume) {
                    clearInterval(volInterval)
                    return
                }

                instance.set("volume", (instance.volume + incr) * this.volume)
            }, 50)
            track.player = instance

            return
        }

        const playOptions = {
            ...(Audio.soundOptions[effect] || {}),
        }

        const volume = (playOptions.volume || 1.0) * this.volume
        await sound.play(effect, {
            ...playOptions,
            volume,
        })
    }

    // stopSoundtrack = () => {
    //     this.activeSoundtrackId = null
    //     this.playingSoundtrack.stop()
    // }

    play = (s: string, options: any = {}) => {
        if (!this.audible()) {
            return
        }
        const volume =
            (options.volume === undefined ? (Audio.soundOptions[s]?.volume || 1.0) * this.volume : options.volume) *
            this.volume
        sound?.play(s, {
            ...options,
            volume,
        })
    }

    playWeatherThunder = () => {
        if (!this.audible() || document.hidden || sound.context.muted || sound.context.paused) return
        const context = sound.context as any
        if (context.audioContext && context.compressor) {
            this.thunder.play(context.audioContext, context.compressor, this.volume * 0.65)
        }
    }
    stopWeatherThunder = () => this.thunder.stop()

    setVolume = (volume: number) => {
        this.thunder.stop()
        this.volume = volume
        Object.values(this.playingSoundtrack).forEach((track: SoundtrackMeta) => {
            if (track.player) {
                const updatedVolume = (Audio.soundOptions[track.trackId]?.volume || 1.0) * volume
                track.player.set("volume", updatedVolume)
            }
        })
    }
}
